import React, { useState, useEffect, useCallback } from "react";
import { Handshake, Search, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "../hooks/useAdminAuth";
import Pagination, { PAGE_SIZE } from "../components/admin/Pagination";
import AdminHeader from "../components/admin/AdminHeader";

const COLORS = {
  bg: "#F4F4F0",
  bgAlt: "#ede5d6",
  ink: "#1a2330",
  inkSoft: "#4a5568",
  inkMuted: "#6b7280",
  dark: "#0e2030",
  card: "#ffffff",
  border: "rgba(15, 30, 45, 0.08)",
  borderStrong: "rgba(15, 30, 45, 0.16)",
};
const SERIF = "'Playfair Display', 'Source Serif Pro', Georgia, 'Times New Roman', serif";
const SANS = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, system-ui, sans-serif";
const MONO = "'JetBrains Mono', 'Fira Code', monospace";

const API_BASE = process.env.REACT_APP_API_URL || "";

const STATUS_STYLES = {
  open:               { label: "Open",               bg: "#e8efea", fg: "#1f6f43", dot: "#2f9b63" },
  awaiting_response:  { label: "Awaiting response",  bg: "#fff3d6", fg: "#8a5a00", dot: "#d4a23a" },
  judged:             { label: "Judged",             bg: "#fdf3d4", fg: "#7a5c00", dot: "#f0c040" },
  resolved:           { label: "Resolved",           bg: "#e3edf7", fg: "#1f4a7a", dot: "#3b78c2" },
  closed:             { label: "Closed",             bg: "#ececec", fg: "#555555", dot: "#888888" },
};

function formatRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function AdminDisputes() {
  const navigate = useNavigate();

  // Authorization header for /api/ai/admin/* — either a Basic Auth session
  // (sessionStorage-backed) or auto-detected via the current user's JWT if
  // they're an is_admin account. See useAdminAuth.
  const {
    authHeader,
    resolving: resolvingAuth,
    loginUser,
    setLoginUser,
    loginPass,
    setLoginPass,
    loginError,
    handleLogin,
    handleAdminLogout,
    handleUnauthorized,
  } = useAdminAuth("/api/ai/admin/disputes");

  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const toggleExpanded = (id) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Per-target resend status, keyed `${disputeId}:${email|all}`.
  const [resendState, setResendState] = useState({});
  const resendInvite = async (disputeId, email) => {
    const key = `${disputeId}:${email || "all"}`;
    setResendState((s) => ({ ...s, [key]: "sending" }));
    try {
      const res = await fetch(`${API_BASE}/api/ai/admin/disputes/${disputeId}/resend-invite`, {
        method: "POST",
        headers: { Authorization: authHeader, "Content-Type": "application/json" },
        body: JSON.stringify(email ? { email } : {}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setResendState((s) => ({ ...s, [key]: "sent" }));
      setTimeout(
        () => setResendState((s) => { const n = { ...s }; delete n[key]; return n; }),
        3000
      );
    } catch (err) {
      setResendState((s) => ({ ...s, [key]: `error:${err.message}` }));
    }
  };

  // Per-dispute save status for the visibility override, keyed by dispute id.
  const [visState, setVisState] = useState({});
  const setVisibility = async (disputeId, value) => {
    setVisState((s) => ({ ...s, [disputeId]: "saving" }));
    try {
      const res = await fetch(`${API_BASE}/api/ai/admin/disputes/${disputeId}/visibility`, {
        method: "PUT",
        headers: { Authorization: authHeader, "Content-Type": "application/json" },
        body: JSON.stringify({ admin_visibility: value }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setDisputes((list) =>
        list.map((d) =>
          d.id === disputeId
            ? {
                ...d,
                admin_visibility: data.admin_visibility,
                visibility_locked: data.visibility_locked,
                flagged_public: data.flagged_public,
                effective_public: data.effective_public,
              }
            : d
        )
      );
      setVisState((s) => ({ ...s, [disputeId]: "saved" }));
      setTimeout(
        () => setVisState((s) => { const n = { ...s }; delete n[disputeId]; return n; }),
        2000
      );
    } catch (err) {
      setVisState((s) => ({ ...s, [disputeId]: `error:${err.message}` }));
    }
  };

  const load = useCallback(async (header) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/ai/admin/disputes`, {
        headers: { Authorization: header },
      });
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setDisputes(data.disputes || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [handleUnauthorized]);

  useEffect(() => {
    if (authHeader) load(authHeader);
  }, [authHeader, load]);

  const filtered = disputes.filter((d) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (d.subject || "").toLowerCase().includes(q) ||
      (d.category || "").toLowerCase().includes(q) ||
      (d.creator_email || "").toLowerCase().includes(q) ||
      (d.disputant_email || "").toLowerCase().includes(q)
    );
  });

  useEffect(() => { setPage(1); }, [search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // While a logged-in user's JWT might still resolve to an is_admin session,
  // hold off on the login form so it doesn't flash before auto-login lands.
  if (resolvingAuth) {
    return (
      <div style={styles.loginRoot}>
        <div style={styles.loginCard}>
          <div style={styles.loginBrand}>
            <Handshake size={26} strokeWidth={1.75} />
            <span style={styles.logo}>disputef<em style={styles.logoItalic}>AI</em></span>
          </div>
          <p style={styles.loginSubtitle}>Checking your session…</p>
        </div>
      </div>
    );
  }

  // ── Login screen ───────────────────────────────────────────────────────────
  if (!authHeader) {
    return (
      <div style={styles.loginRoot}>
        <div style={styles.loginCard}>
          <div style={styles.loginBrand}>
            <Handshake size={26} strokeWidth={1.75} />
            <span style={styles.logo}>disputef<em style={styles.logoItalic}>AI</em></span>
          </div>
          <div style={styles.adminPill}>
            <Shield size={12} strokeWidth={2} style={{ verticalAlign: "middle", marginRight: 4 }} />
            Admin sign-in
          </div>
          <h1 style={styles.loginTitle}>Restricted area</h1>
          <p style={styles.loginSubtitle}>
            This panel uses its own credentials. They are independent from your normal account.
          </p>
          <form onSubmit={handleLogin} style={styles.loginForm}>
            <input
              type="text"
              autoComplete="username"
              placeholder="Admin username"
              value={loginUser}
              onChange={(e) => setLoginUser(e.target.value)}
              style={styles.loginInput}
              autoFocus
            />
            <input
              type="password"
              autoComplete="current-password"
              placeholder="Admin password"
              value={loginPass}
              onChange={(e) => setLoginPass(e.target.value)}
              style={styles.loginInput}
            />
            {loginError && <div style={styles.loginErr}>{loginError}</div>}
            <button type="submit" style={styles.loginBtn}>Enter admin panel</button>
            <button
              type="button"
              onClick={() => navigate("/submit-dispute")}
              style={styles.loginGhost}
            >Back to dashboard</button>
          </form>
        </div>
      </div>
    );
  }

  // ── Authenticated admin view ───────────────────────────────────────────────
  return (
    <div style={styles.root}>
      <AdminHeader active="disputes" onLogout={handleAdminLogout} />

      <main style={styles.main}>
        <div style={styles.pageHeader}>
          <div>
            <h1 style={styles.title}>All disputes (admin)</h1>
            <p style={styles.subtitle}>
              {loading
                ? "Loading…"
                : `${filtered.length} of ${disputes.length} dispute${disputes.length === 1 ? "" : "s"} across all users`}
            </p>
          </div>
          <div style={styles.toolbar}>
            <div style={styles.searchWrap}>
              <Search size={14} strokeWidth={2} style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search subject, category, or email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={styles.search}
              />
            </div>
          </div>
        </div>

        {error && <div style={styles.errorBox}>Failed to load: {error}</div>}

        {!loading && !error && filtered.length === 0 && (
          <div style={styles.emptyState}>
            <h3 style={styles.emptyTitle}>No disputes yet</h3>
            <p style={styles.emptyText}>The platform has no disputes recorded yet.</p>
          </div>
        )}

        {filtered.length > 0 && (
          <div style={styles.list} role="table">
            <div style={styles.listHeader} role="row">
              <div style={{ ...styles.cell, ...styles.cellSubject }}>Subject</div>
              <div style={{ ...styles.cell, ...styles.cellDisputant }}>Disputer</div>
              <div style={{ ...styles.cell, ...styles.cellDisputant }}>Disputant</div>
              <div style={{ ...styles.cell, ...styles.cellRole }}>Participants</div>
              <div style={{ ...styles.cell, ...styles.cellStatus }}>Status</div>
              <div style={{ ...styles.cell, ...styles.cellMeta }}>Turns</div>
              <div style={{ ...styles.cell, ...styles.cellMeta }}>Tokens</div>
              <div style={{ ...styles.cell, ...styles.cellMeta }}>Activity</div>
              <div style={{ ...styles.cell, ...styles.cellAction }}></div>
            </div>

            {paginated.map((d) => {
              const status = STATUS_STYLES[d.dispute_status] || STATUS_STYLES.open;
              const disputerDisplay = d.creator_email || "—";
              const disputantDisplay = d.disputant_email || "—";
              const participants = d.participants || [];
              const isExpanded = expandedIds.has(d.id);
              const isClosed = ["judged", "resolved", "closed"].includes(d.dispute_status);

              return (
                <React.Fragment key={d.id}>
                  <div
                    onClick={() => navigate(`/dispute/${d.uuid}`)}
                    style={styles.row}
                    role="row"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/dispute/${d.uuid}`);
                      }
                    }}
                    aria-label={`Open dispute: ${d.subject}`}
                  >
                    <div style={{ ...styles.cell, ...styles.cellSubject }}>
                      <div style={styles.subjectMain}>
                        {d.subject || "(no subject)"}
                        {d.effective_public && (
                          <span style={styles.publicPill} title="This decision page is readable by anyone with the link">
                            Public
                          </span>
                        )}
                      </div>
                      {d.category && <div style={styles.subjectMeta}>{d.category}</div>}
                    </div>
                    <div style={{ ...styles.cell, ...styles.cellDisputant }}>
                      <span style={styles.disputantEmail} title={d.creator_email || ""}>{disputerDisplay}</span>
                    </div>
                    <div style={{ ...styles.cell, ...styles.cellDisputant }}>
                      <span style={styles.disputantEmail} title={d.disputant_email || ""}>{disputantDisplay}</span>
                    </div>
                    <div style={{ ...styles.cell, ...styles.cellRole }}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleExpanded(d.id); }}
                        style={styles.viewBtn}
                        aria-expanded={isExpanded}
                      >
                        <span style={{ ...styles.caret, transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }}>▸</span>
                        {isExpanded ? "Hide" : "View"} ({participants.length})
                      </button>
                    </div>
                    <div style={{ ...styles.cell, ...styles.cellStatus }}>
                      <span style={{ ...styles.statusBadge, background: status.bg, color: status.fg }}>
                        <span style={{ ...styles.statusDot, background: status.dot }} />
                        {status.label}
                      </span>
                    </div>
                    <div style={{ ...styles.cell, ...styles.cellMeta }}>
                      <span style={styles.turnCount}>{d.turn_count}</span>
                    </div>
                    <div style={{ ...styles.cell, ...styles.cellMeta }}>
                      <span style={styles.tokenCount} title={`${(d.total_tokens || 0).toLocaleString()} AI tokens (panel + judge)`}>
                        {d.total_tokens ? d.total_tokens.toLocaleString() : "—"}
                      </span>
                    </div>
                    <div style={{ ...styles.cell, ...styles.cellMeta }}>
                      <span style={styles.relative}>{formatRelative(d.last_activity_at)}</span>
                    </div>
                    <div style={{ ...styles.cell, ...styles.cellAction }}>
                      <span style={styles.openArrow} aria-hidden>→</span>
                    </div>
                  </div>
                  <div
                    style={{
                      ...styles.expandedRow,
                      maxHeight: isExpanded ? 760 : 0,
                      opacity: isExpanded ? 1 : 0,
                      paddingTop: isExpanded ? 14 : 0,
                      paddingBottom: isExpanded ? 14 : 0,
                      borderBottom: isExpanded ? `1px solid ${COLORS.border}` : "none",
                    }}
                    aria-hidden={!isExpanded}
                  >
                    <div style={styles.expandedHead}>
                      <span style={styles.expandedLabel}>Participants ({participants.length})</span>
                      {participants.length > 0 && !isClosed && (() => {
                        const allKey = `${d.id}:all`;
                        const st = resendState[allKey];
                        return (
                          <button
                            type="button"
                            onClick={() => resendInvite(d.id, null)}
                            disabled={st === "sending"}
                            style={styles.resendAllBtn}
                            title="Resend the invitation email to every invited party"
                          >
                            {st === "sending" ? "Sending…" : st === "sent" ? "All sent ✓" : "✉ Resend all"}
                          </button>
                        );
                      })()}
                      {isClosed && <span style={styles.closedNote}>Dispute closed — invites disabled</span>}
                    </div>
                    {participants.length === 0 ? (
                      <span style={styles.expandedEmpty}>No participants added yet.</span>
                    ) : (
                      <div style={styles.participantList}>
                        {participants.map((p) => {
                          const key = `${d.id}:${p.email}`;
                          const st = resendState[key];
                          const isErr = typeof st === "string" && st.startsWith("error:");
                          return (
                            <div key={p.email} style={styles.participantBlock}>
                              <span style={styles.participantPill}>
                                <span style={styles.participantRoleTag}>{p.role}</span>
                                <span style={styles.participantEmailText}>{p.email}</span>
                                {!isClosed && (
                                  <button
                                    type="button"
                                    onClick={() => resendInvite(d.id, p.email)}
                                    disabled={st === "sending"}
                                    style={{ ...styles.resendBtn, ...(isErr ? styles.resendBtnErr : {}) }}
                                    title={isErr ? st.slice(6) : "Resend invitation email to this party"}
                                  >
                                    {st === "sending"
                                      ? "Sending…"
                                      : st === "sent"
                                      ? "Sent ✓"
                                      : isErr
                                      ? "Retry"
                                      : "Resend"}
                                  </button>
                                )}
                              </span>
                              {p.witnessInfo && (
                                <span style={styles.witnessInfoText}>{p.witnessInfo}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* ── Visibility override ── the platform's superior flag,
                        which wins over whatever the filer chose. ── */}
                    <div style={{ marginTop: 14 }}>
                      <div style={styles.expandedHead}>
                        <span style={styles.expandedLabel}>Public decision page</span>
                      </div>
                      <div style={styles.visibilityRow}>
                        <select
                          value={d.admin_visibility || "none"}
                          onChange={(e) => setVisibility(d.id, e.target.value)}
                          disabled={visState[d.id] === "saving"}
                          style={styles.visibilitySelect}
                          title="Override who may read this dispute's final decision page"
                        >
                          <option value="none">Filer decides</option>
                          <option value="force_public">Force public</option>
                          <option value="force_private">Force private</option>
                        </select>
                        <span style={styles.visibilityState}>
                          Filer chose <strong>{d.is_public ? "public" : "private"}</strong> ·
                          currently{" "}
                          <strong>{d.effective_public ? "public" : "not public"}</strong>
                          {d.flagged_public && !d.effective_public && !isClosed
                            ? " (publishes once judged)"
                            : ""}
                        </span>
                        {visState[d.id] === "saving" && <span style={styles.visibilityState}>Saving…</span>}
                        {visState[d.id] === "saved" && <span style={styles.visibilityState}>Saved ✓</span>}
                        {typeof visState[d.id] === "string" && visState[d.id].startsWith("error:") && (
                          <span style={styles.visibilityErr}>{visState[d.id].slice(6)}</span>
                        )}
                      </div>
                    </div>

                    {(d.provider_usage?.length > 0 || d.judge_usage) && (
                      <div style={{ marginTop: 14 }}>
                        <div style={styles.expandedHead}>
                          <span style={styles.expandedLabel}>AI usage (tokens)</span>
                        </div>
                        <div style={styles.participantList}>
                          {(d.provider_usage || []).map((u) => (
                            <span key={u.provider} style={styles.participantPill}>
                              <span style={styles.participantRoleTag}>{u.provider}</span>
                              <span style={styles.participantEmailText}>
                                {u.model || "default"} · {u.tokens.toLocaleString()} tok
                              </span>
                            </span>
                          ))}
                          {d.judge_usage && (
                            <span style={styles.participantPill}>
                              <span style={styles.participantRoleTag}>judge</span>
                              <span style={styles.participantEmailText}>
                                {d.judge_usage.model || "default"} · {d.judge_usage.tokens.toLocaleString()} tok
                              </span>
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        )}

        <Pagination page={safePage} total={filtered.length} onPageChange={setPage} />
      </main>
    </div>
  );
}

const styles = {
  root: { minHeight: "100vh", background: COLORS.bg, color: COLORS.ink, fontFamily: SANS, fontSize: 14 },

  // ── Login screen ─────────────────────────────────────────────────────────
  loginRoot: {
    minHeight: "100vh", background: COLORS.bg, color: COLORS.ink, fontFamily: SANS,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
  },
  loginCard: {
    width: "100%", maxWidth: 420, background: COLORS.card,
    border: `1px solid ${COLORS.border}`, borderRadius: 12,
    padding: "32px 28px", boxShadow: "0 10px 30px rgba(15, 30, 45, 0.08)",
  },
  loginBrand: { display: "inline-flex", alignItems: "center", gap: 8, color: COLORS.ink, marginBottom: 16 },
  adminPill: {
    display: "inline-block", background: COLORS.dark, color: "#fff",
    padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
    letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 12,
  },
  loginTitle: {
    fontFamily: SERIF, fontSize: 26, fontWeight: 600, margin: "0 0 8px 0",
    letterSpacing: "-0.3px", color: COLORS.ink,
  },
  loginSubtitle: { fontSize: 13, color: COLORS.inkMuted, margin: "0 0 20px 0", lineHeight: 1.5 },
  loginForm: { display: "flex", flexDirection: "column", gap: 12 },
  loginInput: {
    width: "100%", boxSizing: "border-box",
    background: COLORS.bg, color: COLORS.ink,
    border: `1px solid ${COLORS.borderStrong}`, borderRadius: 8,
    padding: "10px 12px", fontSize: 14, fontFamily: SANS, outline: "none",
  },
  loginErr: {
    background: "#fef2f2", color: "#991b1b",
    border: "1px solid #fecaca", borderRadius: 6,
    padding: "8px 10px", fontSize: 12,
  },
  loginBtn: {
    background: COLORS.dark, color: "#fff", border: "none", borderRadius: 8,
    padding: "11px 16px", fontSize: 14, fontWeight: 600, fontFamily: SANS,
    cursor: "pointer",
  },
  loginGhost: {
    background: "transparent", color: COLORS.inkSoft,
    border: `1px solid ${COLORS.border}`, borderRadius: 8,
    padding: "9px 16px", fontSize: 13, fontFamily: SANS, cursor: "pointer",
  },

  // ── Authenticated layout ─────────────────────────────────────────────────
  logo: { fontFamily: SERIF, fontSize: 22, fontWeight: 600, letterSpacing: "-0.3px" },
  logoItalic: { fontStyle: "italic", fontWeight: 600 },

  main: { padding: "32px clamp(16px, 4vw, 48px)", maxWidth: 1280, margin: "0 auto" },

  pageHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-end",
    flexWrap: "wrap", gap: 16, marginBottom: 24,
  },
  title: { fontFamily: SERIF, fontSize: 32, fontWeight: 600, margin: 0, letterSpacing: "-0.5px" },
  subtitle: { fontSize: 13, color: COLORS.inkMuted, margin: "6px 0 0 0" },
  toolbar: { display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" },
  searchWrap: { position: "relative" },
  searchIcon: { position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: COLORS.inkMuted },
  search: {
    background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8,
    padding: "8px 12px 8px 32px", fontSize: 13, fontFamily: SANS, color: COLORS.ink,
    outline: "none", minWidth: 260,
  },

  errorBox: {
    padding: 16, background: "#fef2f2", color: "#991b1b",
    border: "1px solid #fecaca", borderRadius: 8, marginBottom: 16,
  },
  emptyState: {
    textAlign: "center", padding: "60px 24px", background: COLORS.card,
    border: `1px solid ${COLORS.border}`, borderRadius: 12,
  },
  emptyTitle: { fontFamily: SERIF, fontSize: 22, margin: "0 0 8px 0", color: COLORS.ink },
  emptyText: { color: COLORS.inkMuted, margin: 0, fontSize: 13 },

  list: {
    background: COLORS.card, border: `1px solid ${COLORS.border}`,
    borderRadius: 12, overflow: "hidden",
  },
  listHeader: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.6fr) minmax(0, 1.2fr) minmax(0, 1.2fr) 1.1fr 1.1fr 0.5fr 0.7fr 0.8fr 32px",
    gap: 16, padding: "12px 20px",
    background: COLORS.bgAlt, color: COLORS.inkMuted,
    fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5,
    borderBottom: `1px solid ${COLORS.border}`,
  },
  row: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.6fr) minmax(0, 1.2fr) minmax(0, 1.2fr) 1.1fr 1.1fr 0.5fr 0.7fr 0.8fr 32px",
    gap: 16, padding: "16px 20px",
    background: COLORS.card, color: COLORS.ink, textAlign: "left",
    border: "none", borderBottom: `1px solid ${COLORS.border}`,
    cursor: "pointer", fontFamily: SANS, fontSize: 13,
    transition: "background 0.15s",
    alignItems: "center", width: "100%",
  },
  cell: { minWidth: 0, display: "flex", alignItems: "center" },
  cellSubject: { flexDirection: "column", alignItems: "flex-start", gap: 4 },
  cellDisputant: {},
  cellRole: {},
  cellStatus: {},
  cellMeta: { color: COLORS.inkMuted, fontSize: 12 },
  cellAction: { justifyContent: "flex-end" },

  subjectMain: {
    fontWeight: 600, fontSize: 14, color: COLORS.ink,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    maxWidth: "100%",
  },
  subjectMeta: {
    display: "flex", alignItems: "center", gap: 6, color: COLORS.inkMuted, fontSize: 12,
  },
  disputantEmail: {
    fontFamily: MONO, fontSize: 12, color: COLORS.inkSoft,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    maxWidth: "100%",
  },
  statusBadge: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600,
    whiteSpace: "nowrap",
  },
  statusDot: { width: 6, height: 6, borderRadius: "50%" },
  turnCount: {
    fontFamily: MONO, fontSize: 12, fontWeight: 600, color: COLORS.ink,
    background: COLORS.bgAlt, padding: "2px 8px", borderRadius: 6,
  },
  tokenCount: { fontFamily: MONO, fontSize: 12, color: COLORS.inkSoft, whiteSpace: "nowrap" },
  relative: { fontFamily: MONO, fontSize: 12 },
  openArrow: { color: COLORS.inkMuted, fontSize: 16 },

  viewBtn: {
    display: "inline-flex", alignItems: "center", gap: 6,
    background: "transparent", color: COLORS.ink,
    border: `1px solid ${COLORS.border}`, borderRadius: 6,
    padding: "4px 10px", fontSize: 12, fontWeight: 600, fontFamily: SANS,
    cursor: "pointer", transition: "background 0.15s, border-color 0.15s",
  },
  caret: {
    display: "inline-block", fontSize: 10, color: COLORS.inkMuted,
    transition: "transform 0.18s ease",
  },
  expandedRow: {
    background: COLORS.bgAlt,
    paddingLeft: 24, paddingRight: 24,
    overflow: "hidden",
    transition: "max-height 0.22s ease, opacity 0.18s ease, padding 0.18s ease",
  },
  expandedHead: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: 12, marginBottom: 8, flexWrap: "wrap",
  },
  expandedLabel: {
    fontSize: 10, fontWeight: 700, color: COLORS.inkMuted,
    letterSpacing: 1.5, textTransform: "uppercase",
  },
  resendAllBtn: {
    display: "inline-flex", alignItems: "center", gap: 6,
    background: COLORS.dark, color: "#fff", border: "none", borderRadius: 6,
    padding: "5px 12px", fontSize: 12, fontWeight: 600, fontFamily: SANS, cursor: "pointer",
  },
  closedNote: { fontSize: 11, color: COLORS.inkMuted, fontStyle: "italic" },
  publicPill: {
    marginLeft: 8, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
    letterSpacing: 1, background: "#e3edf7", color: "#1f4a7a",
    padding: "2px 8px", borderRadius: 999, verticalAlign: "middle",
  },
  visibilityRow: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  visibilitySelect: {
    background: COLORS.bgAlt, color: COLORS.ink,
    border: `1px solid ${COLORS.borderStrong}`, borderRadius: 8,
    padding: "5px 10px", fontSize: 12, fontFamily: SANS, cursor: "pointer",
  },
  visibilityState: { fontSize: 12, color: COLORS.inkMuted },
  visibilityErr: { fontSize: 12, color: "#991b1b" },
  resendBtn: {
    background: COLORS.bgAlt, color: COLORS.ink,
    border: `1px solid ${COLORS.borderStrong}`, borderRadius: 999,
    padding: "2px 10px", fontSize: 11, fontWeight: 600, fontFamily: SANS,
    cursor: "pointer", marginLeft: 2,
  },
  resendBtnErr: { background: "#fef2f2", color: "#991b1b", borderColor: "#fecaca" },
  expandedEmpty: { fontSize: 12, color: COLORS.inkMuted, fontStyle: "italic" },
  participantList: { display: "flex", flexWrap: "wrap", gap: 8 },
  participantPill: {
    display: "inline-flex", alignItems: "center", gap: 8,
    background: COLORS.card, border: `1px solid ${COLORS.border}`,
    borderRadius: 999, padding: "4px 10px 4px 4px", fontSize: 12,
  },
  participantRoleTag: {
    fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1,
    background: COLORS.dark, color: "#fff",
    padding: "2px 8px", borderRadius: 999,
  },
  participantEmailText: { fontFamily: MONO, fontSize: 12, color: COLORS.inkSoft },
  participantBlock: { display: "flex", flexDirection: "column", gap: 3, maxWidth: 360 },
  witnessInfoText: { fontSize: 11, color: COLORS.inkMuted, lineHeight: 1.4, padding: "0 4px" },
};
